import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound } from "@/pages/ErrorPages";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function StorefrontLogin() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const { login } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [tenantStatus, setTenantStatus] = useState("loading");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/tenants/${slug}`)
      .then((r) => {
        if (!cancelled) {
          setTenant(r.data);
          setTenantStatus("ready");
        }
      })
      .catch(() => !cancelled && setTenantStatus("not_found"));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (tenantStatus === "loading") return null;
  if (tenantStatus === "not_found" || !tenant) return <NotFound />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await login(email, password);
      const role = data.user.role;
      if (role === "subscriber" && data.tenant_slug === slug) {
        // Phase 9 — subscribers always go to the profile picker first.
        const target = next ? sp(`/select-profile?next=${encodeURIComponent(next)}`) : sp(`/select-profile`);
        navigate(target, { replace: true });
      } else if (role === "subscriber" && data.tenant_slug !== slug) {
        setError(`This account belongs to "${data.tenant_slug}". Sign in there instead.`);
      } else if (role === "tenant_admin") {
        navigate("/studio", { replace: true });
      } else if (role === "super_admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const themeColor = tenant.theme_color;

  return (
    <StorefrontLayout tenant={tenant}>
      <div className="max-w-md mx-auto px-6 py-20" data-testid="storefront-login-page">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Subscribers</p>
        <h1 className="mt-3 font-display font-bold text-4xl tracking-tighter">Sign in to {tenant.name}</h1>
        <p className="mt-3 text-white/55 font-light text-sm">
          Don't have an account?{" "}
          <Link to={sp(`/signup`)} className="underline decoration-white/30 underline-offset-4 hover:decoration-white text-white" data-testid="storefront-login-signup-link">
            Subscribe here.
          </Link>
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="storefront-login-form">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.3em] text-white/50">Email</Label>
            <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white h-11" data-testid="storefront-login-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.3em] text-white/50">Password</Label>
            <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white h-11" data-testid="storefront-login-password" />
          </div>
          {error && (
            <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="storefront-login-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-none uppercase tracking-[0.25em] text-xs"
            style={{ backgroundColor: themeColor, color: "#0a0a0a" }}
            data-testid="storefront-login-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Sign in to ${tenant.name}`}
          </Button>
        </form>
      </div>
    </StorefrontLayout>
  );
}
